import FingerprintJS from '@fingerprintjs/fingerprintjs';
import { load as loadBotD } from '@fingerprintjs/botd';
import { detectIncognito } from 'detectincognitojs';
import { nanoid } from 'nanoid';
import { isValidRedirectURL } from './utils';
import Logger from './logger';
import { captureError } from './posthog';
import { version } from '../package.json';

// Fetch server time and calculate offset
const getServerUnixTime = async () => {
  const fallbackDate = Math.floor(new Date().getTime() / 1000);
  try {
    const response = await fetch('https://gateway.clickshield.24metrics.com/ping', {
      method: 'GET',
      cache: 'no-store'
    });

    if (!response.ok) {
      console.warn(`Failed to fetch server time (HTTP ${response.status}), using local time`);
      return fallbackDate;
    }

    const data = await response.json();

    if (!data.server_time_utc) {
      console.warn('Server time response missing server_time_utc field, using local time');
      return fallbackDate;
    }

    // server_time_utc is already a Unix timestamp in seconds
    return data.server_time_utc;
  } catch (error) {
    captureError(error, { method: 'getServerUnixTime' });
    console.warn('Failed to fetch server time, using local time:', error?.message || error);
    return fallbackDate;
  }
};

const getBrowserTimezone = () => {
  if (typeof Intl !== 'undefined') {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  }
  const offset = new Date().getTimezoneOffset();
  const sign = offset <= 0 ? '+' : '-';
  const hours = String(Math.abs(Math.trunc(offset / 60))).padStart(2, '0');
  const minutes = String(Math.abs(offset % 60)).padStart(2, '0');
  return `UTC${sign}${hours}:${minutes}`;
};

const runJSChecks = async (serverUnixTime) => {
  const jsCheckData = {
    fingerprint: null,
    js_bot_found: false,
    incognito_detected: false,
    js_start_time: new Date(serverUnixTime * 1000).toISOString().slice(0, 19).replace('T', ' '),
    js_browser_timezone: getBrowserTimezone(),
  };

  try {
    const getUserFingerprint = async () => {
      const fp = await FingerprintJS.load();
      const result = await fp.get();
      return result.visitorId;
    };

    // Asynchronously detect if the visitor is a bot.
    const getBotDetectionResult = async () => {
      const botd = await loadBotD();
      const result = botd.detect();
      // Here we're assuming `isBot` is a property in the result that indicates bot detection status.
      // Adjust based on the actual API response.
      return result.bot.probability > 0.5; // Assuming a threshold for bot detection, adjust as necessary;
    };

    const getIncognitoDetectionResult = async () => {
      const result = await detectIncognito();
      return result.isPrivate;
    };

    const results = await Promise.allSettled([
      getUserFingerprint(),
      getBotDetectionResult(),
      getIncognitoDetectionResult(),
    ]);

    const [fingerprintResult, botResult, incognitoResult] = results;

    if (fingerprintResult.status === 'rejected') {
      captureError(fingerprintResult.reason, { method: 'runJSChecks', check: 'fingerprint' });
    }
    if (botResult.status === 'rejected') {
      captureError(botResult.reason, { method: 'runJSChecks', check: 'botDetection' });
    }
    if (incognitoResult.status === 'rejected') {
      captureError(incognitoResult.reason, { method: 'runJSChecks', check: 'incognitoDetection' });
    }

    jsCheckData.fingerprint = fingerprintResult.status === 'fulfilled' ? fingerprintResult.value : null;
    jsCheckData.js_bot_found = botResult.status === 'fulfilled' ? botResult.value : false;
    jsCheckData.incognito_detected = incognitoResult.status === 'fulfilled' ? incognitoResult.value : false;
  } catch (error) {
    captureError(error, { method: 'runJSChecks' });
  }

  try {
    if (window.localStorage) {
      const getUUID = () => {
        const savedUuid = localStorage.getItem('cs-uuid-device');
        if (savedUuid) {
          return savedUuid;
        }
        const id = nanoid();
        localStorage.setItem('cs-uuid-device', id);
        return id;
      };
      jsCheckData.unique_user_id = getUUID();
    }
  } catch {
    // localStorage access denied (cross-origin iframe, restricted privacy settings, etc.)
  }

  return jsCheckData;
};

// This function creates the URL for the ASP service.
// It takes the service name and parameters as input and build the correct API url.
// It also automatically adds all query parameters from the current page URL to the API url for tracking purposes.
const createASPURL = (service, params, opts) => {
  const logger = new Logger(opts);
  // If params.integrationID is not provided, return an error
  if (!params.integrationID) {
    logger.error('ASP.createASPURL error; integrationID is required.');
    return null;
  }
  
  let apiHTTPURL = `https://${params.integrationID}.gateway.go2link.co`;
  if (params.aspEnv) {
    switch (params.aspEnv) {
      case 'staging':
        apiHTTPURL = `https://${params.integrationID}.gateway-staging.go2link.co`;
        break;
      case 'local':
        apiHTTPURL = `http://localhost:8080/${service}/integration/${params.integrationID}`;
        break;
      default:
        logger.log(`Invalid ASP ${params.aspEnv} environment. Using production environment.`);
    }
    delete params.aspEnv;
  }

  if (apiHTTPURL.includes('go2link.co')) {
    switch (service) {
      case 'clicks':
        apiHTTPURL += '/c'
        break;
      case 'postbacks':
        apiHTTPURL += '/api/event'
        break;
      case 'imp':
        apiHTTPURL += '/api/impression'
        break;
      default:
        logger.log(`Invalid service ${service}.`);
    }
  }

  delete params.integrationID;

  const httpURLWithParams = new URL(apiHTTPURL);
  const currentPageURLParamsURLParams = new URLSearchParams(window.location.search);

  Object.entries(params).forEach(([key, value]) => {
    logger.log(`Adding param ${key} with value ${value}`);
    currentPageURLParamsURLParams.set(key, value);
  });
  httpURLWithParams.search = currentPageURLParamsURLParams.toString();

  return httpURLWithParams.href;
};

const setCookie = (key, value) => {
  const hostname = window.location.hostname;
  const parts = hostname.split('.');
  let domain = '';

  // Handle cases with more than two parts (e.g., sub.example.com, app.example.co.uk)
  if (parts.length > 2) {
    // For standard TLDs (com, org, net, etc.) take the last two parts
    if (parts.slice(-1)[0].length > 1) {
      domain = `.${parts.slice(-2).join('.')}`;
    }
    // For complex TLDs (co.uk, com.au, etc.), try taking the last three parts
    else if (parts.length > 3) {
      domain = `.${parts.slice(-3).join('.')}`;
    }
    // Fallback to the last two if the above heuristics don't clearly apply
    else {
      domain = `.${parts.slice(-2).join('.')}`;
    }
  }
  // Handle cases with exactly two parts (e.g., example.com)
  else if (parts.length === 2) {
    domain = `.${hostname}`;
  }
  // Handle single part (e.g., localhost) - domain will be the host
  else {
    domain = hostname;
  }

  const days = 30;
  const date = new Date();
  date.setTime(date.getTime() + (days * 24 * 60 * 60 * 1000));
  const expires = `expires=${date.toUTCString()}`;

  document.cookie = `${key}=${value}; path=/; ${expires}; domain=${domain};`;
};

const getCookie = (name) => {
  try {
    return document.cookie
      .split('; ')
      .find((row) => row.startsWith(`${name}=`))
      ?.split('=')[1];
  } catch (error) {
    captureError(error, { method: 'getCookie' });
    return null;
  }
};

var ASP = {
  Version: version,
  get24metricsData: async () => {
    try {
      const serverUnixTime = await getServerUnixTime();
      const jsCheckData = await runJSChecks(serverUnixTime);
      // Create a base64 encoded string from the jsCheckData object.
      const base64Data = btoa(JSON.stringify(jsCheckData));
      return base64Data;
    } catch (error) {
      captureError(error, { method: 'get24metricsData' });
      return null;
    }
  },

  getQueryParam: (param) => {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get(param);
  },

  getUTMParams: () => {
    const params = new URLSearchParams(window.location.search);
    return {
      publisher: params.get('utm_source') || '',
      offer: params.get('utm_campaign') || '',
      advertiser: params.get('utm_medium') || '',
      keyword: params.get('utm_term') || '',
      content: params.get('utm_content') || '',
      external_transaction_id: params.get('gclid') || ''
    };
  },

  // Redirects the user considering that the current page is accessed with /jsp path.
  // This function will remove the /jsp path from the URL and append the necessary query parameters.
  // This function will add all query parameters from the current URL to the new URL.
  redirectClick: async () => {
    try {
      const serverUnixTime = await getServerUnixTime();
      const currentURL = new URL(window.location.href);
      currentURL.pathname = currentURL.pathname.replace('/jsp', '');
      const queryParams = new URLSearchParams(window.location.search);
      const jsCheckData = await runJSChecks(serverUnixTime);
      Object.entries(jsCheckData).forEach(([key, value]) => {
        queryParams.set(key, value);
      });
      const clickTime = String(serverUnixTime);
      queryParams.set('click_time', clickTime);
      setCookie('asp_click_time', clickTime);

      currentURL.search = queryParams.toString();
      window.location.replace(currentURL.href);
    } catch (error) {
      captureError(error, { method: 'redirectClick' });
    }
  },

  makeImpression: async (impressionURL, opts) => {
    const logger = new Logger(opts);
    try {
      const serverUnixTime = await getServerUnixTime();
      const baseURL = new URL(impressionURL);
      const queryParams = new URLSearchParams(baseURL.search);
      const jsCheckData = await runJSChecks(serverUnixTime);
      Object.entries(jsCheckData).forEach(([key, value]) => {
        queryParams.set(key, value);
      });
      const impressionTime = String(serverUnixTime);
      queryParams.set('impression_time', impressionTime);
      setCookie('asp_impression_time', impressionTime);
      baseURL.search = queryParams.toString();

      // Make the impression request
      return await fetch(baseURL.href, {
        method: 'GET',
      })
        .then((response) => Promise.all([Promise.resolve(response.status), response.json()]))
        .then(([status, data]) => {
          if (status === 200) {
            setCookie('asp_transaction_id', data.transaction_id);
          }
          const res = { status, data };
          logger.log(res);
          return res;
        })
        .catch((error) => {
          // SyntaxError here means response.json() failed to parse a non-JSON body (e.g. HTML page).
          // This is expected when tracking URLs return a website instead of JSON.
          // Chrome throws SyntaxError; Safari throws DOMException with name "SyntaxError".
          if (error.name === 'SyntaxError') {
            return undefined;
          }
          captureError(error, { method: 'makeImpression' });
          logger.warn(error);
        });
    } catch (error) {
      captureError(error, { method: 'makeImpression' });
      logger.warn(error);
    }
  },

  makeClick: async (clickURL, opts) => {
    const logger = new Logger(opts);
    try {
      const serverUnixTime = await getServerUnixTime();
      const baseURL = new URL(clickURL);
      const queryParams = new URLSearchParams(baseURL.search);
      const jsCheckData = await runJSChecks(serverUnixTime);
      Object.entries(jsCheckData).forEach(([key, value]) => {
        queryParams.set(key, value);
      });
      queryParams.set('no_redirect', 'true');
      const clickTime = String(serverUnixTime);
      queryParams.set('click_time', clickTime);
      setCookie('asp_click_time', clickTime);
      baseURL.search = queryParams.toString();

      // Make the click request
      return await fetch(baseURL.href, {
        method: 'GET',
      })
        .then((response) => Promise.all([Promise.resolve(response.status), response.json()]))
        .then(([status, data]) => {
          if (status === 200) {
            setCookie('asp_transaction_id', data.transaction_id);
          }
          return { status, data };
        })
        .catch((error) => {
          // SyntaxError here means response.json() failed to parse a non-JSON body (e.g. HTML page).
          // This is expected when tracking URLs return a website instead of JSON.
          // Chrome throws SyntaxError; Safari throws DOMException with name "SyntaxError".
          if (error.name === 'SyntaxError') {
            return undefined;
          }
          captureError(error, { method: 'makeClick' });
          logger.warn(error);
        });
    } catch (error) {
      captureError(error, { method: 'makeClick' });
      logger.warn(error);
    }
  },

  makeConversion: async (conversionURL, opts) => {
    const logger = new Logger(opts);
    try {
      const serverUnixTime = await getServerUnixTime();
      const baseURL = new URL(conversionURL);
      const queryParams = new URLSearchParams(baseURL.search);
      const jsCheckData = await runJSChecks(serverUnixTime);
      Object.entries(jsCheckData).forEach(([key, value]) => {
        queryParams.set(key, value);
      });
      queryParams.set('use_current_time', 'true');

      // If the params contain transaction_id, we will use the lookup service to create a conversion.
      // When this is used, the service just needs to know the transaction_id and the conversion will be created.
      if (!queryParams.has('transaction_id')) {
        const transactionID = getCookie('asp_transaction_id');
        if (transactionID) {
          queryParams.set('transaction_id', transactionID);
        } else {
          logger.error('Transaction ID not found in cookie.');
        }
      }

      if (queryParams.has('transaction_id')) {
        baseURL.pathname = '/api/event-tx';
      } else {
        const clickTime = getCookie('asp_click_time');
        if (clickTime) {
          queryParams.set('click_time', clickTime);
        }
      }

      baseURL.search = queryParams.toString();

      // Make the conversion request
      return await fetch(baseURL.href, {
        method: 'GET',
      })
        .then((response) => Promise.all([Promise.resolve(response.status), response.json()]))
        .then(([status, data]) => {
          const res = { status, data };
          logger.log(res);
          return res;
        })
        .catch((error) => {
          // SyntaxError here means response.json() failed to parse a non-JSON body (e.g. HTML page).
          // This is expected when tracking URLs return a website instead of JSON.
          // Chrome throws SyntaxError; Safari throws DOMException with name "SyntaxError".
          if (error.name === 'SyntaxError') {
            return undefined;
          }
          captureError(error, { method: 'makeConversion' });
          logger.warn(error);
        });
    } catch (error) {
      captureError(error, { method: 'makeConversion' });
      logger.warn(error);
    }
  },

  trackClick: async (params, opts) => {
    const integrationID = params.integrationID;
    try {
      const httpURL = createASPURL('clicks', params, opts);
      const click = await ASP.makeClick(httpURL, opts);
      return click;
    } catch (error) {
      captureError(error, { method: 'trackClick', integration_id: integrationID });
      return undefined;
    }
  },

  trackImpression: async (params, opts) => {
    const integrationID = params.integrationID;
    try {
      const logger = new Logger(opts);
      // Get UTM params and override the params if they exist.
      const utmParams = ASP.getUTMParams();
      if (utmParams.publisher) {
        logger.log('using utm params.publisher:', utmParams.publisher);
        params.publisher = utmParams.publisher;
      }
      if (utmParams.offer) {
        logger.log('using utm params.offer:', utmParams.offer);
        params.offer = utmParams.offer;
      }
      if (utmParams.advertiser) {
        logger.log('using utm params.advertiser:', utmParams.advertiser);
        params.advertiser = utmParams.advertiser;
      }
      if (utmParams.keyword) {
        logger.log('using utm params.keyword:', utmParams.keyword);
        params.sub_id = utmParams.keyword;
      }
      if (utmParams.content) {
        logger.log('using utm params.content:', utmParams.content);
        params.content = utmParams.content;
      }
      if (utmParams.external_transaction_id) {
        logger.log('using utm params.external_transaction_id:', utmParams.external_transaction_id);
        params.external_transaction_id = utmParams.external_transaction_id;
      }
      if (document.referrer) {
        params.referer = document.referrer;
      }
      const httpURL = createASPURL('imp', params, opts);
      const impression = await ASP.makeImpression(httpURL, opts);
      return impression;
    } catch (error) {
      captureError(error, { method: 'trackImpression', integration_id: integrationID });
      return undefined;
    }
  },

  trackConversion: async (params, opts) => {
    const integrationID = params.integrationID;
    try {
      const httpURL = createASPURL('postbacks', params, opts);
      const conversion = await ASP.makeConversion(httpURL, opts);
      return conversion;
    } catch (error) {
      captureError(error, { method: 'trackConversion', integration_id: integrationID });
      return undefined;
    }
  },

  // This function is used to track clicks on the page.
  // It will call the trackClick function and pass the parameters to it.
  // Handles redirects for both approved and rejected clicks.
  pageClick: (params, opts) => {
    const integrationID = params.integrationID;
    const logger = new Logger(opts);
    // Get UTM params and override the params if they exist.
    const utmParams = ASP.getUTMParams();
    if (utmParams.publisher) {
      logger.log('using utm params.publisher:', utmParams.publisher);
      params.publisher = utmParams.publisher;
    }
    if (utmParams.offer) {
      logger.log('using utm params.offer:', utmParams.offer);
      params.offer = utmParams.offer;
    }
    if (utmParams.advertiser) {
      logger.log('using utm params.advertiser:', utmParams.advertiser);
      params.advertiser = utmParams.advertiser;
    }
    if (utmParams.keyword) {
      logger.log('using utm params.keyword:', utmParams.keyword);
      params.sub_id = utmParams.keyword;
    }
    if (utmParams.content) {
      logger.log('using utm params.content:', utmParams.content);
      params.content = utmParams.content;
    }
    if (utmParams.external_transaction_id) {
      logger.log('using utm params.external_transaction_id:', utmParams.external_transaction_id);
      params.external_transaction_id = utmParams.external_transaction_id;
    }
    if (document.referrer) {
      params.referer = document.referrer;
    }

    // These fields are required to activate the logic for monitoring mode and fallback url redirection.
    params.fields = "monitoring_mode,redirect_url";

    ASP.trackClick(params, opts).then(function (response) {
      logger.log(response);
      if (!response || response.status != 200) {
        // If the response is missing or the status is not 200, we don't do anything as per requirements
        logger.log("Click not successful, not clearing page content");
        return;
      }

      const redirectURL = response.data.redirect_url;
      const hasRedirectUrlFromResponse = redirectURL && redirectURL != "";
      const isRedirectUrlSafe = hasRedirectUrlFromResponse && isValidRedirectURL(redirectURL, window.location.href);

      if (response.data.status == "approved") {
        // Handle redirect for approved clicks

        if (hasRedirectUrlFromResponse) {
          logger.log("Click approved, found redirect url:", redirectURL);
        }
        if (isRedirectUrlSafe) {
          logger.log("Redirecting to Approved redirect url:", redirectURL);
          window.location.replace(redirectURL);
        } else {
          logger.log("Click approved, not clearing page content");
        }
        return;
      }

      // From here, the click is rejected already.

      if (response.data.monitoring_mode == 1) {
        // In monitoring mode, still allow redirect if URL is provided
        if (hasRedirectUrlFromResponse) {
          logger.log("Monitoring mode is 1, found redirect url:", redirectURL);
        }
        if (isRedirectUrlSafe) {
          logger.log("Redirecting in monitoring mode:", redirectURL);
          window.location.replace(redirectURL);
        } else {
          logger.log("Monitoring mode is 1, not clearing page content");
        }
        return;
      }

      if (response.data.is_blocked === true) {
        // Clear the page content
        logger.log("Blocked click, clearing page content");
        document.body.innerHTML = '<p>24M Blocked Click</p>';
        return;
      }

      // Click rejected. Redirect the user if fallback url is provided.
      if (hasRedirectUrlFromResponse) {
        logger.log("found fallback url:", redirectURL);
      }
      if (isRedirectUrlSafe) {
        logger.log("Redirecting to fallback URL:", redirectURL);
        window.location.replace(redirectURL);
      }
    }).catch(function (error) {
      captureError(error, { method: 'pageClick', integration_id: integrationID });
      // If there's an error, we don't do anything as per requirements
      logger.error("Error in ASP.trackClick:", error);
      // Page loads normally if there's an error
    });
  },
};


window.ASP = ASP;

export default ASP;
